import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useModalStore } from '../../stores/modal';
import {
  sitesApi,
  Site,
  extractApiError,
  SITE_LIFECYCLE_LABELS,
  getSiteLifecycleBadgeTone,
} from '../../lib/organizations';
import { siteIndustryLabel } from '../../lib/siteIndustry';
import type { SiteStatus } from '@safetyhub/shared';
import { useSites } from '../../hooks/useSites';
import { usePermissions } from '../../hooks/usePermissions';
import AgencySiteLinkEntry from '../../components/agency-links/AgencySiteLinkEntry';
import { useAuthStore } from '../../stores/auth';
import { useGroupStore } from '../../stores/group';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { useToastStore } from '../../stores/toast';
import SiteFormModal from '../../components/modals/SiteFormModal';
import SiteTransferModal from '../../components/modals/SiteTransferModal';
import { cancelTransfer, getTransfers, SiteTransfer } from '../../lib/site-transfer';
import { formatDateToLocal } from '@safetyhub/shared';
import { formatDayDistance } from '../../utils/relativeDay';
import {
  Badge,
  Button,
  Card,
  Modal,
  ROW_ACTION_TRIGGER_CLASS,
  TABLE_HEADER_CELL_CLASS,
} from '@safetyhub/ui';
import type { BadgeTone } from '@safetyhub/ui';
import {
  PlusIcon,
  AlertTriangleIcon as ExclamationIcon,
  XIcon,
  SearchIcon,
  EllipsisHorizontalIcon,
  ArrowPathIcon,
  ArrowsUpDownIcon,
  CheckCircleIcon,
  ClockIcon,
  MapPinIcon,
  // 통계 카드 아이콘은 목업 아이콘 세트에서 온다. 이 파일 하단의 로컬 `BuildingIcon`은
  // 글리프가 달라(표 타일·빈 상태에서 쓰는 기존 것) alias로 갈라 둔다 — 표 본문은 이미
  // 목업에 정렬된 면이라 이번 범위에서 건드리지 않는다.
  BuildingIcon as StatBuildingIcon,
} from '../../components/icons';
import { StatTile } from '../../components/StatTile';
import { FilterPill } from '../../components/FilterPill';
import { SearchHotkeyBadge, useSearchHotkey } from '../../components/SearchHotkey';
import {
  activeAssignees,
  buildSiteStats,
  countInspectedToday,
  countRegisteredThisQuarter,
  countSitesWithoutPrimary,
  filterSites,
  findPrimaryAssignee,
  getSiteInspectionStatus,
  lifecycleOptions,
  regionOptions,
  SITE_SORT_OPTIONS,
  sortSites,
  type SiteSort,
} from './sitesView';
// 아바타 팔레트는 멤버 화면이 정본이다 — 같은 사람이 두 화면에서 다른 색이면 구분 기능이 깨지므로
// 팔레트를 복제하지 않는다. (공용 위치 승격은 이번 범위 밖 — 메인 판단)
import { PersonAvatar } from '../../components/PersonAvatar';
import { isSiteInfoLocked, siteInfoLockedReason } from '../../lib/siteInfoManage';

// 이 표는 축이 둘이다: **상태 칸 = 저장된 생애주기**(`Site.status`), **마지막 점검 칸 = 점검 흐름**
// (`getSiteInspectionStatus` 파생). 예전에는 한 칸이 둘을 섞고 거기에 최신 보고서의 결재 상태까지
// 얹혀 있었다(결정 기록 `2026-08-03-site-status-axes`). 파생은 통계 카드도 같은 값을 읽어야 해서
// 순수 함수 층(`sitesView.ts`)에 있다.

/**
 * 점검 상태 색 이름 → 공용 Badge tone 매핑.
 * `getSiteInspectionStatus`의 반환 계약(color 문자열)은 그대로 두고 표시 계층에서만 변환한다
 * — 데스크톱 표와 모바일 카드가 같은 매핑을 공유한다.
 * 목업 세만틱 5색(blue/emerald/amber/rose/violet)에 수렴시키므로 yellow·orange는 함께 amber.
 */
/**
 * 담당자 공백 라벨 — 표·카드·상단 요약이 같은 말을 쓴다.
 * 세 곳이 각자 문구를 들면 같은 상태를 다른 이름으로 부르게 된다.
 */
const SITE_PRIMARY_VACANT_LABEL = '담당자 미지정';

function getSiteStatusBadgeTone(color: string): BadgeTone {
  const tones: Record<string, BadgeTone> = {
    gray: 'slate',
    blue: 'blue',
    purple: 'violet',
    yellow: 'amber',
    orange: 'amber',
    green: 'emerald',
    red: 'rose',
  };
  return tones[color] || 'slate';
}

/**
 * 점검 배지는 **지금 무언가 돌고 있을 때만** 단다. '완료'·'대기'는 옆의 날짜(또는 '없음')가
 * 이미 같은 말을 하므로, 배지를 달면 모든 행이 배지를 갖게 돼 신호가 죽는다
 * (신호 없으면 미표시 규약).
 */
const INSPECTION_IN_FLIGHT_LABELS = new Set(['점검중', '검토중']);

// 데스크톱 리스킨 공통 클래스(표 헤더 셀·행 액션 트리거)는 `@safetyhub/ui`의 controlClasses로
// 승격했다. 행 액션 트리거는 승격하면서 hover 그림자를 목업 값(`shadow-control-hover`)으로 정정.

/**
 * 현장 타일 색 — 목업 `.site-pic`의 4색(blue/green/violet/amber)을 현장마다 다르게 준다.
 * 멤버 아바타와 같은 이유의 **값 구분 팔레트**이고, 한 색으로 통일하면 행 구분이 사라진다.
 * 멈춘 현장(일시중지·종료)은 팔레트에서 빼고 slate로 떨어뜨린다 — 회색이 "지금 안 쓰는 현장"을
 * 뜻하던 기존 신호를 색 다양화가 덮어쓰면 정보가 준다. 이관중은 아직 운용 중인 현장이라
 * 컬러 타일을 유지한다.
 * (Tailwind 정적 스캔 대상 — 완성형 리터럴 유지)
 */
const SITE_TILE_GRADIENT = [
  'bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700',
  'bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700',
  'bg-gradient-to-br from-violet-100 to-violet-200 text-violet-700',
  'bg-gradient-to-br from-amber-100 to-amber-200 text-amber-700',
] as const;

const SITE_TILE_INACTIVE = 'bg-gradient-to-br from-ink-100 to-ink-200 text-ink-400';

function getSiteTileClass(site: Site): string {
  if (site.status === 'PAUSED' || site.status === 'CLOSED') return SITE_TILE_INACTIVE;
  let hash = 0;
  for (let i = 0; i < site.id.length; i += 1) {
    hash = (hash * 31 + site.id.charCodeAt(i)) % 100000;
  }
  return SITE_TILE_GRADIENT[hash % SITE_TILE_GRADIENT.length];
}

export default function SitesList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentMembership } = useAuthStore();
  const currentGroupId = useGroupStore((s) => s.currentGroupId);
  const { openCreateSite, openEditSite } = useModalStore();
  const { canManageSites, can } = usePermissions();
  const companyId = currentMembership?.companyId;
  const layout = useDeviceLayout();
  const isMobile = layout === 'phone' || layout === 'fold';
  const { showToast } = useToastStore();

  // React Query로 현장 목록 조회
  const { data: sites = [], isLoading, isFetching, error: queryError } = useSites(companyId, currentGroupId);
  const [error, setError] = useState('');

  // 툴바(목업 `.toolbar`) — 목록은 이미 전량 로드돼 있어 검색·필터·정렬은 클라이언트에서 건다.
  // 데스크톱에서만 렌더하므로 모바일에서는 초기값(빈 검색어·전체) 그대로 전체 목록이 보인다.
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<SiteStatus | ''>('');
  const [regionFilter, setRegionFilter] = useState('');
  // 기본값은 빈 값 = 서버 순서(등록 최신순) — 이 화면이 예전과 같은 순서로 열린다.
  const [sort, setSort] = useState<SiteSort>('');
  // ⌘K/Ctrl+K → 검색창 포커스(시안 .kbd 배지와 짝).
  const searchRef = useSearchHotkey();

  // 삭제 모달
  const [deleteTarget, setDeleteTarget] = useState<Site | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // 이관 모달
  const [transferTarget, setTransferTarget] = useState<Site | null>(null);

  // PENDING 이관 목록 (siteId → transferId 매핑)
  const [pendingTransfers, setPendingTransfers] = useState<Record<string, string>>({});

  // 현장 이관(요청·취소·목록) = `site.transfer` — 서버 `SiteTransferService.assertCanTransfer`와 같은 키. 2026-09-24 정책 반영으로
  // `site.assign`(배정·해제·타입 변경 — 시드 safety-manager도 가짐)에서 갈랐다: 이관은 조직 밖으로 현장을 내보내는 되돌리기 어려운
  // 행위라 (총괄)책임자·owner에만. `site.assign`을 그대로 읽으면 안전관리자에게 메뉴가 보이고 서버는 403이다.
  const canTransfer = can('site.transfer');

  // 새로고침 버튼이 이관 상태도 함께 다시 읽어야 해서 effect 본문을 콜백으로 뺐다 —
  // 목록만 갱신하면 '이관중' 배지가 방금 취소된 상태를 계속 보여 준다.
  const loadPendingTransfers = useCallback(() => {
    if (!companyId || !canTransfer) {
      setPendingTransfers({});
      return;
    }
    getTransfers(companyId).then((transfers: SiteTransfer[]) => {
      const map: Record<string, string> = {};
      transfers.forEach((t) => {
        if (t.status === 'PENDING') {
          map[t.siteId] = t.id;
        }
      });
      setPendingTransfers(map);
    }).catch(() => {
      // 이관 목록 조회 실패 시 무시
    });
  }, [companyId, canTransfer]);

  useEffect(() => {
    loadPendingTransfers();
  }, [loadPendingTransfers]);

  // 에러 메시지 통합
  const displayError = error || (queryError instanceof Error ? queryError.message : '');

  // 상태 필터는 상태 칸과 **같은 축**(생애주기)이어야 고른 값과 배지가 맞아떨어진다.
  // 값은 enum, 표시만 라벨이라 문구를 바꿔도 필터가 깨지지 않는다.
  const statusOptions = useMemo(() => lifecycleOptions(sites), [sites]);

  const regions = useMemo(() => regionOptions(sites), [sites]);

  // 카드는 **필터 이전 전체** 기준이다 — 목록을 좁힐 때마다 요약이 따라 줄면
  // "우리 조직에 현장이 몇 곳인가"라는 카드 본래의 물음에 답하지 못한다.
  const stats = useMemo(() => buildSiteStats(sites), [sites]);
  const registeredThisQuarter = useMemo(
    () => countRegisteredThisQuarter(sites, new Date()),
    [sites],
  );
  const inspectedToday = useMemo(() => countInspectedToday(sites, new Date()), [sites]);
  // 담당자 공백도 같은 규약(필터 이전 전체) — 필터로 가려진 현장의 공백이 요약에서 사라지면
  // 경고가 조건부로만 보이게 된다.
  const sitesWithoutPrimary = useMemo(() => countSitesWithoutPrimary(sites), [sites]);

  const visibleSites = useMemo(
    () => sortSites(filterSites(sites, { query, region: regionFilter, status: statusFilter }), sort),
    [sites, query, regionFilter, statusFilter, sort],
  );

  const isFiltered = query.trim().length > 0 || statusFilter !== '' || regionFilter !== '';

  const handleDelete = async () => {
    if (!deleteTarget || !companyId) return;

    setIsDeleting(true);
    try {
      await sitesApi.delete(companyId, deleteTarget.id);
      queryClient.invalidateQueries({ queryKey: ['sites', companyId] });
      setDeleteTarget(null);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelTransfer = async (e: React.MouseEvent, siteId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const transferId = pendingTransfers[siteId];
    if (!transferId) return;
    try {
      await cancelTransfer(transferId);
      showToast('이관 요청이 취소되었습니다.', 'success');
      setPendingTransfers((prev) => {
        const next = { ...prev };
        delete next[siteId];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['sites', companyId] });
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    }
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['sites', companyId] });
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['sites', companyId] });
    loadPendingTransfers();
  };

  if (!companyId) {
    return (
      <div className="text-center py-12">
        <p className="font-medium text-ink-500">조직을 선택해주세요.</p>
      </div>
    );
  }

  return (
    <div className={isMobile ? 'space-y-4' : 'space-y-6'}>
      {/* 헤더 */}
      {isMobile ? (
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold tracking-tight text-ink-900">현장 관리</h1>
          <div className="flex items-center gap-2">
            <AgencySiteLinkEntry size="sm" />
            {canManageSites && (
              <Button
                variant="primary"
                size="sm"
                iconOnly
                onClick={openCreateSite}
                aria-label="현장 추가"
              >
                <PlusIcon className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-end justify-between gap-6">
          <div>
            <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">현장 관리</h1>
            <p className="mt-1 text-[15px] font-medium text-ink-500">
              조직에 등록된 현장을 관리합니다.
            </p>
          </div>
          {/* 목업 `.page-actions` — 새로고침(아이콘) + 현장 추가. 새로고침은 좁은 화면에서
              숨긴다(모바일은 당겨서 새로고침이 있고, 헤더 폭도 버튼 두 개를 못 받는다). */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              aria-label="목록 새로고침"
              title="새로고침"
              className="hidden sm:grid h-[38px] w-[38px] place-items-center rounded-[10px] border border-line bg-white text-ink-700 transition-colors hover:bg-surface-hover"
            >
              {/* 다시 읽는 동안 아이콘이 돈다 — 목록이 그대로면 눌린 게 맞나 알 수 없다 */}
              <ArrowPathIcon className={`h-[17px] w-[17px] ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            {/* 대행 현장 연결 입구 — 권한(`site.manage` ∨ OWNER)이 없으면 아무것도 그리지 않는다 */}
            <AgencySiteLinkEntry />
            {canManageSites && (
              <Button variant="primary" onClick={openCreateSite}>
                <PlusIcon className="h-5 w-5" />
                현장 추가
              </Button>
            )}
          </div>
        </div>
      )}

      {/* 에러 메시지 */}
      {displayError && (
        <div className="rounded-card border border-rose-200 bg-rose-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationIcon className="h-5 w-5 text-rose-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-rose-700">{displayError}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError('')}
                className="inline-flex text-rose-400 transition-colors hover:text-rose-600"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 통계 카드 4장(목업 `.stats`) — 데스크톱·모바일 공통. 이 페이지에는 카드와 짝지을 탭이
          없어 전부 표시 전용이다(onSelect 없음). pill은 flow 지표(기간 내 유입·해소)만 단다 —
          stock 지표(미해결 건수 등)의 전기간 대비는 과거 스냅샷 없이는 계산 자체가 안 된다.
          pill 종류는 "늘어난 게 좋은 일인가"로 갈린다: 신규 등록·해소는 diff(증가=emerald),
          위험요인 유입은 alert(rose) — 증가가 나쁜 수를 초록으로 칠하지 않기 위해서다.
          값을 못 읽었을 때 0으로 속이지 않고 '—'로 두는 건 결재함·문서관리 카드와 같은 규약이다. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="전체 현장"
          value={isLoading ? '—' : String(stats.total)}
          unit={isLoading ? undefined : '곳'}
          foot={
            !isLoading && registeredThisQuarter > 0
              ? `이번 분기 ${registeredThisQuarter}곳 신규 등록`
              : undefined
          }
          // 시안 `+1` pill = 이번 분기 신규(같은 flow 지표) — 0이면 pill 자체를 넘기지 않는다.
          pill={
            !isLoading && registeredThisQuarter > 0
              ? { kind: 'diff', diff: registeredThisQuarter }
              : undefined
          }
          icon={StatBuildingIcon}
          accentClass="text-brand-600"
          iconBgClass="bg-brand-50"
        />
        <StatTile
          label="점검 진행 중"
          value={isLoading ? '—' : String(stats.inspecting)}
          unit={isLoading ? undefined : '곳'}
          // 시안 "금일 점검 N건 진행" — 마지막 점검일이 오늘인 현장 수로 근사. 0이면 숨김.
          foot={
            !isLoading && inspectedToday > 0 ? `금일 점검 ${inspectedToday}건 진행` : undefined
          }
          icon={ClockIcon}
          accentClass="text-amber-600"
          iconBgClass="bg-amber-50"
        />
        <StatTile
          label="미해결 위험요인"
          value={isLoading ? '—' : String(stats.unresolvedTotal)}
          unit={isLoading ? undefined : '건'}
          // 목업의 '시급 대응 필요 N건' = 미해결 중 위험도 최상위 등급 건수. 긴급도 축을 새로
          // 만든 게 아니라 위험성평가 config의 판정(3×3 '높음' / 3단계 '상')을 그대로 읽는다.
          foot={
            !isLoading && stats.unresolvedHighTotal > 0
              ? `시급 대응 필요 ${stats.unresolvedHighTotal}건`
              : undefined
          }
          // 시안 rose `+5` — 최근 7일 유입. 발견이 느는 건 나쁜 신호라 증감(diff) pill을 쓰지
          // 않는다(diff는 증가를 emerald로 칠한다). 0이면 pill 자체를 넘기지 않는다.
          pill={
            !isLoading && stats.found7dTotal > 0
              ? { kind: 'alert', label: `+${stats.found7dTotal}` }
              : undefined
          }
          icon={ExclamationIcon}
          accentClass="text-rose-600"
          iconBgClass="bg-rose-50"
        />
        {/* 목업 4번째 카드는 '평균 안전 등급'인데 등급 도메인이 없어(현장 점수·등급 자체가
            없다) 같은 자리에 **위험요인 해결률**을 둔다. 카드 넷이 모두 위험요인을 말하지
            않도록, 앞 카드가 남은 양이면 이 카드는 처리된 비율을 읽는다. */}
        <StatTile
          label="위험요인 해결률"
          value={isLoading || stats.resolvedRate === null ? '—' : String(stats.resolvedRate)}
          unit={isLoading || stats.resolvedRate === null ? undefined : '%'}
          foot={
            !isLoading && stats.findingsTotal > 0
              ? `해결 ${stats.resolvedTotal} / 전체 ${stats.findingsTotal}건`
              : undefined
          }
          // 시안 up pill — 최근 7일 해소. 이쪽은 늘어나는 게 좋은 신호라 diff(증가=emerald)가 맞다.
          pill={
            !isLoading && stats.resolved7dTotal > 0
              ? { kind: 'diff', diff: stats.resolved7dTotal }
              : undefined
          }
          icon={CheckCircleIcon}
          accentClass="text-emerald-600"
          iconBgClass="bg-emerald-50"
        />
      </div>

      {/* 담당자 공백 요약 — 카드 4장은 목업 규격(2×2 / 1×4)이라 다섯 번째를 끼우면 격자가
          깨진다. 상태가 **조치를 요구하는 경고**라는 점에서도 중립 카드보다 이 페이지가 이미
          쓰는 알림 띠(위 에러 블록)의 amber 판이 맞다. 0곳이면 표시하지 않는다(신호 없으면
          미표시 규약) — 상시 노출되는 띠는 곧 배경이 된다. */}
      {!isLoading && sitesWithoutPrimary > 0 && (
        <div className="flex items-start gap-3 rounded-card border border-amber-200 bg-amber-50 p-4">
          <ExclamationIcon className="h-5 w-5 flex-none text-amber-500" />
          <p className="text-sm font-medium text-amber-700">
            {SITE_PRIMARY_VACANT_LABEL} 현장이{' '}
            <span className="font-bold tabular-nums">{sitesWithoutPrimary}</span>곳 있습니다. 현장
            상세에서 정 담당자를 지정해 주세요.
          </p>
        </div>
      )}

      {/* 로딩 */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : sites.length === 0 ? (
        /* 빈 상태 */
        <Card radius="lg" padding="none" className="p-12 text-center">
          <BuildingIcon className="mx-auto h-12 w-12 text-ink-400" />
          <h3 className="mt-4 text-lg font-bold text-ink-900">현장이 없습니다</h3>
          <p className="mt-2 text-sm font-medium text-ink-500">
            새 현장을 추가하여 점검 및 보고서를 관리하세요.
          </p>
          {canManageSites && (
            <div className="mt-6">
              <Button variant="primary" className="mx-auto" onClick={openCreateSite}>
                <PlusIcon className="h-5 w-5" />
                첫 현장 추가하기
              </Button>
            </div>
          )}
        </Card>
      ) : isMobile ? (
        /* 모바일 — 카드 레이아웃.
           ⚠️ `sites`가 아니라 `visibleSites`다. 예전엔 원본 배열을 렌더해서 **폰에서는
           검색·필터·정렬이 목록에 전혀 반영되지 않았다**(데스크톱 테이블은 `visibleSites`를
           쓰고 있어 같은 화면이 기기에 따라 다른 답을 냈다). 2026-08-22 리뷰. */
        <div className="space-y-3">
          {/* 필터 결과가 0이면 빈 화면 대신 이유를 말해 준다(데스크톱 표와 같은 문구). */}
          {visibleSites.length === 0 && (
            <p className="px-4 py-12 text-center text-sm font-medium text-ink-400">
              조건에 맞는 현장이 없습니다.
            </p>
          )}
          {visibleSites.map((site) => {
            const primaryAssignee = findPrimaryAssignee(site);
            // '외 N명'도 공백 판정과 같은 축 — 떠난 멤버십의 잔존 배정을 세면
            // '담당자 미지정' 배지 옆에서 인원이 잡히는 자기모순이 된다.
            const assigneeCount = activeAssignees(site).length;
            const inspectionCount = site._count?.inspections || 0;
            const lastInspection = site.inspections?.[0];
            const inspectionStatus = getSiteInspectionStatus(site);
            const isTransferPending = site.status === 'TRANSFER_PENDING';

            return (
              <Card
                key={site.id}
                className="cursor-pointer"
                onClick={() => navigate(`/sites/${site.id}`)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold tracking-tight text-ink-900 truncate">{site.name}</p>
                    {siteIndustryLabel(site) && (
                      <p className="mt-0.5 text-xs font-medium text-ink-500">{siteIndustryLabel(site)}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* 현장 자체의 상태(생애주기) — 이관대기·비활성 별도 배지를 여기로 흡수했다 */}
                    <Badge tone={getSiteLifecycleBadgeTone(site.status)} dot>
                      {SITE_LIFECYCLE_LABELS[site.status]}
                    </Badge>
                    {canManageSites && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <SiteDropdownMenu
                          canTransfer={canTransfer}
                          isTransferPending={isTransferPending}
                          editLockedReason={isSiteInfoLocked(site) ? siteInfoLockedReason() : null}
                          onEdit={(e) => { e.preventDefault(); e.stopPropagation(); openEditSite(site.id); }}
                          onTransfer={(e) => { e.preventDefault(); e.stopPropagation(); setTransferTarget(site); }}
                          onCancelTransfer={(e) => handleCancelTransfer(e, site.id)}
                          onDelete={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget(site); }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs font-medium text-ink-500">
                  {/* 담당자 공백은 회색 글자가 아니라 경고 배지다 — 정 담당자가 없으면 그 현장의
                      점검·결재가 멈춘다(서버도 OWNER에게 SITE_PRIMARY_NEEDED를 쏜다).
                      데스크톱 표와 같은 tone·문구를 쓴다(한쪽만 고치면 다른 화면에서 재발). */}
                  {primaryAssignee?.member?.member?.name ? (
                    <span>
                      {primaryAssignee.member.member.name}
                      {assigneeCount > 1 ? ` 외 ${assigneeCount - 1}명` : ''}
                    </span>
                  ) : (
                    <Badge tone="amber" size="sm" dot>
                      {SITE_PRIMARY_VACANT_LABEL}
                    </Badge>
                  )}
                  <span className="tabular-nums">{inspectionCount}회</span>
                  <span className="tabular-nums">
                    마지막 점검: {lastInspection ? formatDateToLocal(new Date(lastInspection.createdAt)) : '없음'}
                  </span>
                  {INSPECTION_IN_FLIGHT_LABELS.has(inspectionStatus.label) && (
                    <span title={inspectionStatus.description}>
                      <Badge tone={getSiteStatusBadgeTone(inspectionStatus.color)}>
                        {inspectionStatus.label}
                      </Badge>
                    </span>
                  )}
                </div>
                {site.findingStats && site.findingStats.total > 0 && (
                  <div className="mt-2 flex items-center gap-1">
                    {site.findingStats.unresolved > 0 ? (
                      <>
                        <span className="text-xs font-bold tabular-nums text-rose-600">
                          미해소 {site.findingStats.unresolved}
                        </span>
                        <span className="text-xs font-semibold tabular-nums text-ink-400">/ {site.findingStats.total}건</span>
                      </>
                    ) : (
                      <>
                        <span className="text-xs font-bold text-emerald-600">전체 해소</span>
                        <span className="text-xs font-semibold tabular-nums text-ink-400">({site.findingStats.total}건)</span>
                      </>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        /* 데스크톱 — 툴바 + 테이블 레이아웃 */
        <>
        {/* 툴바 — 목업 `.toolbar`(검색 + 필터) */}
        <Card radius="lg" padding="none" className="flex flex-wrap items-center gap-2.5 px-4 py-3.5">
          <div className="flex h-[38px] min-w-[240px] flex-1 items-center gap-2 rounded-control border border-line bg-surface-soft px-3 transition-colors focus-within:border-brand-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-50">
            <SearchIcon className="h-4 w-4 flex-none text-ink-400" />
            <input
              ref={searchRef}
              type="text"
              aria-label="현장 검색"
              placeholder="현장명, 담당자, 지역으로 검색"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="min-w-0 flex-1 bg-transparent text-[15px] text-ink-900 outline-none placeholder:text-ink-400"
            />
            <SearchHotkeyBadge />
          </div>
          {/* 목업 `.filter-dd` — 지역·상태. 지역 선택지는 주소 첫 토큰에서 파생하므로
              별도 지역 필드를 만들지 않고도 지금 목록에 있는 지역만 고를 수 있다.
              주소가 하나도 없으면 고를 것이 없어 pill 자체를 띄우지 않는다. */}
          {regions.length > 0 && (
            <FilterPill
              icon={MapPinIcon}
              label="지역"
              ariaLabel="지역 필터"
              value={regionFilter}
              options={regions.map((region) => ({ value: region, label: region }))}
              onChange={setRegionFilter}
            />
          )}
          <FilterPill
            icon={CheckCircleIcon}
            label="상태"
            ariaLabel="상태 필터"
            value={statusFilter}
            options={statusOptions.map((status) => ({
              value: status,
              label: SITE_LIFECYCLE_LABELS[status],
            }))}
            onChange={(value) => setStatusFilter(value as SiteStatus | '')}
          />
        </Card>

        <Card radius="lg" padding="none" className="overflow-x-auto">
          {/* 표 카드 헤더 — 목업 .table-card-h. 표와 같은 min-w를 둬 가로 스크롤 시 함께 움직인다 */}
          <div className="flex min-w-[900px] items-center justify-between gap-4 border-b border-line-soft bg-gradient-to-b from-white to-surface-soft px-4 py-2.5">
            <div className="text-sm font-semibold text-ink-500">
              총 <span className="font-bold tabular-nums text-ink-800">{visibleSites.length}</span>곳의 현장 관리 중
              {isFiltered && <span className="ml-1 text-ink-400">(전체 {sites.length}곳 중)</span>}
            </div>
            {/* 목업은 "정렬:" 글자 + pill이지만, FilterPill이 이미 라벨 칸을 갖고 있어 라벨을
                '정렬'로 두고 바깥 글자는 생략한다(같은 말을 두 번 적지 않는다).
                빈 값은 좁히기가 아니라 "고르지 않음"이라 태그를 '기본'으로 바꾼다. */}
            <FilterPill
              icon={ArrowsUpDownIcon}
              label="정렬"
              ariaLabel="목록 정렬"
              value={sort}
              options={SITE_SORT_OPTIONS}
              onChange={(value) => setSort(value as SiteSort)}
              allLabel="기본"
            />
          </div>
          <table className="min-w-[900px] w-full">
            <thead>
              <tr>
                <th className={`${TABLE_HEADER_CELL_CLASS} min-w-[260px]`}>
                  현장명
                </th>
                <th className={TABLE_HEADER_CELL_CLASS}>
                  담당자
                </th>
                <th className={TABLE_HEADER_CELL_CLASS}>
                  점검
                </th>
                <th className={`${TABLE_HEADER_CELL_CLASS} min-w-[120px]`}>
                  마지막 점검
                </th>
                <th className={`${TABLE_HEADER_CELL_CLASS} min-w-[150px]`}>
                  위험요인
                </th>
                <th className={TABLE_HEADER_CELL_CLASS}>
                  상태
                </th>
                <th className={`${TABLE_HEADER_CELL_CLASS} text-right`}>
                  <span className="sr-only">작업</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-soft bg-white">
              {visibleSites.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm font-medium text-ink-400">
                    조건에 맞는 현장이 없습니다.
                  </td>
                </tr>
              )}
              {visibleSites.map((site) => {
                const primaryAssignee = findPrimaryAssignee(site);
                // '외 N명'도 공백 판정과 같은 축 — 떠난 멤버십의 잔존 배정을 세면
                // '담당자 미지정' 배지 옆에서 인원이 잡히는 자기모순이 된다.
                const assigneeCount = activeAssignees(site).length;
                const inspectionCount = site._count?.inspections || 0;
                const lastInspection = site.inspections?.[0];
                const inspectionStatus = getSiteInspectionStatus(site);
                const isTransferPending = site.status === 'TRANSFER_PENDING';

                return (
                  <tr
                    key={site.id}
                    onClick={() => navigate(`/sites/${site.id}`)}
                    className="cursor-pointer transition-colors hover:bg-surface-soft"
                  >
                    {/* 현장명 */}
                    <td className="px-4 py-3.5 min-w-[260px]">
                      <div className="flex items-center gap-3 min-w-0">
                        {/* 목업 .site-pic — 46px 그라디언트 타일. 현장별 4색 + 비활성은 slate */}
                        <div
                          className={`grid h-11 w-11 flex-none place-items-center rounded-card ${getSiteTileClass(site)}`}
                        >
                          <BuildingIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          {/* 이관대기·비활성 배지는 상태 칸의 생애주기 배지로 흡수했다(같은 말 두 번 X) */}
                          <p className="text-[15px] font-bold tracking-tight text-ink-900 truncate">{site.name}</p>
                          {/* 목업 .site-info-s — 업종 · 주소. 가운뎃점 구분자는 둘 다 있을 때만 */}
                          {(siteIndustryLabel(site) || site.address) && (
                            <p className="mt-0.5 flex items-center gap-1.5 text-xs font-medium text-ink-500 min-w-0">
                              {siteIndustryLabel(site) && <span className="flex-none">{siteIndustryLabel(site)}</span>}
                              {siteIndustryLabel(site) && site.address && (
                                <span aria-hidden="true" className="h-[2px] w-[2px] flex-none rounded-full bg-ink-300" />
                              )}
                              {site.address && <span className="truncate">{site.address}</span>}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    {/* 담당자 — 목업 .mgr(이니셜 아바타 + 이름) */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      {primaryAssignee?.member?.member?.name ? (
                        <div className="flex items-center gap-2">
                          <PersonAvatar
                            name={primaryAssignee.member.member.name}
                            seed={primaryAssignee.member.member.id}
                            size="sm"
                          />
                          <span className="text-sm font-semibold text-ink-800">
                            {primaryAssignee.member.member.name}
                            {assigneeCount > 1 && (
                              <span className="ml-1 text-xs font-medium text-ink-400">외 {assigneeCount - 1}명</span>
                            )}
                          </span>
                        </div>
                      ) : (
                        // 회색 '미지정'은 "빈 값"으로 읽혀 지나쳤다 — 조치가 필요한 상태라
                        // 경고 배지로 세운다(모바일 카드와 같은 tone·문구).
                        <Badge tone="amber" dot>
                          {SITE_PRIMARY_VACANT_LABEL}
                        </Badge>
                      )}
                    </td>
                    {/* 점검 횟수 */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className="text-lg font-extrabold tabular-nums tracking-tight text-ink-900">
                        {inspectionCount}
                      </span>
                      <span className="ml-1 text-xs font-semibold text-ink-400">회</span>
                    </td>
                    {/* 마지막 점검 — 목업 .date: 절대 날짜 + 상대시간 보조행.
                        점검 흐름(점검중/검토중)이 이 칸에 배지로 붙는다 — 상태 칸이 현장
                        생애주기를 맡으면서 점검 축은 그 대상인 '마지막 점검'으로 옮겼다. */}
                    <td className="px-4 py-3.5 whitespace-nowrap min-w-[120px]">
                      {lastInspection ? (
                        (() => {
                          const inspectedAt = new Date(lastInspection.createdAt);
                          const relative = formatDayDistance(inspectedAt);
                          return (
                            <div className="flex flex-col items-start gap-1">
                              <span className="text-sm font-semibold tabular-nums tracking-tight text-ink-800">
                                {formatDateToLocal(inspectedAt)}
                              </span>
                              {relative && (
                                <span className="text-xs font-medium text-ink-400">{relative}</span>
                              )}
                              {INSPECTION_IN_FLIGHT_LABELS.has(inspectionStatus.label) && (
                                <span title={inspectionStatus.description}>
                                  <Badge tone={getSiteStatusBadgeTone(inspectionStatus.color)}>
                                    {inspectionStatus.label}
                                  </Badge>
                                </span>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <span className="text-sm font-medium text-ink-300">없음</span>
                      )}
                    </td>
                    {/* 위험요인 집계 — 목업 .risk-cell(수치 + 미해소 비율 막대) */}
                    <td className="px-4 py-3.5 whitespace-nowrap min-w-[150px]">
                      {site.findingStats && site.findingStats.total > 0 ? (
                        <div className="flex flex-col gap-1.5">
                          {site.findingStats.unresolved > 0 ? (
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-sm font-bold tabular-nums text-rose-600">
                                미해소 {site.findingStats.unresolved}
                              </span>
                              <span className="text-xs font-semibold tabular-nums text-ink-400">
                                / 전체 {site.findingStats.total}건
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-sm font-bold text-emerald-600">전체 해소</span>
                              <span className="text-xs font-semibold tabular-nums text-ink-400">({site.findingStats.total}건)</span>
                            </div>
                          )}
                          {/* 막대는 수치의 시각 반복이라 보조기술에는 숨긴다(위 텍스트가 같은 값을 읽어준다).
                              색은 의미색 — 미해소가 남으면 amber→rose, 전량 해소면 emerald. */}
                          <div
                            aria-hidden="true"
                            className="h-1.5 w-full overflow-hidden rounded-full bg-line"
                          >
                            <div
                              className={`h-full rounded-full ${
                                site.findingStats.unresolved > 0
                                  ? 'bg-gradient-to-r from-amber-400 to-rose-400'
                                  : 'bg-gradient-to-r from-emerald-400 to-emerald-500'
                              }`}
                              style={{
                                width: `${
                                  site.findingStats.unresolved > 0
                                    ? Math.max(
                                        4,
                                        Math.round(
                                          (site.findingStats.unresolved / site.findingStats.total) * 100,
                                        ),
                                      )
                                    : 100
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm font-medium text-ink-300">-</span>
                      )}
                    </td>
                    {/* 상태 뱃지 — 현장 생애주기(저장 값). 점검·결재 진행은 여기 오지 않는다. */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <Badge tone={getSiteLifecycleBadgeTone(site.status)} dot>
                        {SITE_LIFECYCLE_LABELS[site.status]}
                      </Badge>
                    </td>
                    {/* 드롭다운 메뉴 */}
                    <td className="px-4 py-3.5 whitespace-nowrap text-right">
                      {canManageSites && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <SiteDropdownMenu
                            canTransfer={canTransfer}
                            isTransferPending={isTransferPending}
                            triggerClassName={ROW_ACTION_TRIGGER_CLASS}
                            editLockedReason={isSiteInfoLocked(site) ? siteInfoLockedReason() : null}
                            onEdit={(e) => { e.preventDefault(); e.stopPropagation(); openEditSite(site.id); }}
                            onTransfer={(e) => { e.preventDefault(); e.stopPropagation(); setTransferTarget(site); }}
                            onCancelTransfer={(e) => handleCancelTransfer(e, site.id)}
                            onDelete={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget(site); }}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
        </>
      )}

      {/* 삭제 확인 모달 — 아이콘+제목이 본문 머리라 base 헤더를 쓰지 않는다(종전에도 ✕가 없었다).
          삭제 중에는 닫는 길을 전부 막는다 — 종전엔 오버레이 클릭으로 도중에 닫혔다. */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        ariaLabel="현장 삭제"
        size="md"
        showCloseButton={false}
        closeOnOverlay={!isDeleting}
        closeOnEsc={!isDeleting}
        closeLocked={isDeleting}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              취소
            </Button>
            <Button
              variant="danger"
              data-testid="site-delete-confirm"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? '삭제 중...' : '삭제'}
            </Button>
          </>
        }
      >
        {deleteTarget && (
              <div className="flex items-start">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <ExclamationIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-ink-900">현장 삭제</h3>
                  <p className="mt-2 text-sm text-ink-500">
                    <strong>{deleteTarget.name}</strong> 현장을 삭제하시겠습니까?
                    <br />
                    이 작업은 되돌릴 수 없으며, 관련된 모든 점검 및 보고서 데이터가 함께 삭제됩니다.
                  </p>
                </div>
              </div>
        )}
      </Modal>

      {/* 현장 생성/수정 모달 */}
      <SiteFormModal onSuccess={handleSuccess} />

      {/* 현장 이관 모달 */}
      {transferTarget && companyId && (
        <SiteTransferModal
          isOpen={!!transferTarget}
          onClose={() => setTransferTarget(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['sites', companyId] });
          }}
          siteId={transferTarget.id}
          siteName={transferTarget.name}
          companyId={companyId}
        />
      )}
    </div>
  );
}

// 현장 드롭다운 메뉴
interface SiteDropdownMenuProps {
  /** 현장 이관·이관 취소(`site.transfer`) — 메뉴 자체는 `canManageSites`(site.manage) 안에서만 렌더된다 */
  canTransfer: boolean;
  isTransferPending: boolean;
  /**
   * 「수정」을 비활성으로 둘 사유(O10 — 정보 관리권이 업체에 있음, `isSiteInfoLocked`) — `null`이면 열려 있다.
   * 감추지 않는다: 업체가 활성 멤버를 모두 잃으면 풀리는 막힘이라 사유를 보여야 한다(이관 대기 비활성과 같은 형태).
   */
  editLockedReason: string | null;
  onEdit: (e: React.MouseEvent) => void;
  onTransfer: (e: React.MouseEvent) => void;
  onCancelTransfer?: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  /** 트리거(⋯) 버튼 클래스. 기본값은 모바일 카드용 톤 — 데스크톱 표는 목업 행 액션 톤을 넘긴다. */
  triggerClassName?: string;
}

const DEFAULT_DROPDOWN_TRIGGER_CLASS =
  'rounded-card-sm p-1.5 text-ink-400 transition-colors hover:bg-surface-hover hover:text-ink-700';

function SiteDropdownMenu({ canTransfer, isTransferPending, editLockedReason, onEdit, onTransfer, onCancelTransfer, onDelete, triggerClassName = DEFAULT_DROPDOWN_TRIGGER_CLASS }: SiteDropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 140;
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - menuWidth,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const openedAt = performance.now();
    const handleScroll = () => {
      if (performance.now() - openedAt < 250) return;
      setIsOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={triggerClassName}
        aria-label="현장 메뉴"
      >
        <EllipsisHorizontalIcon className="h-5 w-5" />
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          data-testid="site-dropdown-menu"
          style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left }}
          className="z-[9999] py-1 bg-white rounded-card border border-line shadow-card min-w-[180px]"
        >
          {isTransferPending ? (
            <button
              type="button"
              disabled
              title="이관 대기 중인 현장은 수정할 수 없습니다."
              className="w-full px-4 py-2 text-sm font-medium text-left text-ink-300 cursor-not-allowed flex items-center gap-2"
            >
              <span>수정</span>
            </button>
          ) : editLockedReason !== null ? (
            <button
              type="button"
              disabled
              title={editLockedReason}
              className="w-full px-4 py-2 text-sm font-medium text-left text-ink-300 cursor-not-allowed flex items-center gap-2"
            >
              <span>수정</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => { onEdit(e); setIsOpen(false); }}
              className="w-full px-4 py-2 text-sm font-medium text-left text-ink-700 hover:bg-surface-hover flex items-center gap-2"
            >
              <span>수정</span>
            </button>
          )}
          {canTransfer && !isTransferPending && (
            <button
              type="button"
              onClick={(e) => { onTransfer(e); setIsOpen(false); }}
              className="w-full px-4 py-2 text-sm font-medium text-left text-ink-700 hover:bg-surface-hover flex items-center gap-2"
            >
              <span>이관</span>
            </button>
          )}
          {canTransfer && isTransferPending && onCancelTransfer && (
            <button
              type="button"
              onClick={(e) => { onCancelTransfer(e); setIsOpen(false); }}
              className="w-full px-4 py-2 text-sm font-medium text-left text-amber-600 hover:bg-amber-50 flex items-center gap-2"
            >
              <span>이관 취소</span>
            </button>
          )}
          <div className="h-px bg-line-soft my-1" />
          {isTransferPending ? (
            <button
              type="button"
              disabled
              title="이관 대기 중인 현장은 삭제할 수 없습니다."
              className="w-full px-4 py-2 text-sm font-medium text-left text-ink-300 cursor-not-allowed flex items-center gap-2"
            >
              <span>삭제</span>
            </button>
          ) : (
            <button
              type="button"
              data-testid="site-dropdown-delete"
              onClick={(e) => { onDelete(e); setIsOpen(false); }}
              className="w-full px-4 py-2 text-sm font-medium text-left text-rose-600 hover:bg-rose-50 flex items-center gap-2"
            >
              <span>삭제</span>
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

// Icons
function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}
